import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {ButtonGroup} from 'shells/button-group/button-group';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
// @ts-expect-error - imported from .js/.jsx file
import {CogIcon} from 'shells/icon/svg/cog-icon';
import {ViewBoardIcon as BoardIcon} from 'shells/icon/svg/view-board-icon';
import {ViewListIcon as ListIcon} from 'shells/icon/svg/view-list-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';

import {appHistory} from '../../history';

import {GetInstanceInfo} from '../core/get-instance-info';
import {useGetSessionUser} from '../core/use-get-session-user';
import {PageContainer, PageTitleBar} from '../layout';

import {CreatePageButton} from './create-page/create-page-button';
import {useGetLandingPages} from './graphql/hooks';
import {LandingPagesBoard} from './landing-pages-board';
import {LandingPagesTable} from './landing-pages-table';
import {LandingPagesUpsellBanner} from './upsell-banner/landing-pages-upsell-banner';

type Props = ContextRouter & {
    activeFilters: FilterObject[];

    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    onUpdateFilters: (arg1: FilterObject) => void;

    onRemoveAllFilters: () => void;
};

export function LandingPagesListPage(props: Props) {
    const selectedTab = props.urlParams.view || 'board';

    // Support ?create=true from command palette AI action
    const shouldAutoCreate = props.urlParams.create === 'true';

    React.useEffect(() => {
        if (shouldAutoCreate) {
            // Clear the param so refresh doesn't re-trigger
            props.onParamChange({create: undefined}, true);
        }
    }, [shouldAutoCreate]); // eslint-disable-line react-hooks/exhaustive-deps

    const showArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';
    const {landingPages, isLoading, error} = useGetLandingPages(showArchived);

    const tabOptions = [
        {
            label: 'Board',
            id: 'board',
            icon: BoardIcon,
            href: props.getUpdatedUrl({
                view: 'board',
            }),
            component: (
                <LandingPagesBoard
                    landingPages={landingPages}
                    isLoading={isLoading}
                    error={error}
                    archived={showArchived}
                />
            ),
        },
        {
            label: 'Table',
            id: 'table',
            icon: ListIcon,
            href: props.getUpdatedUrl({
                view: 'table',
            }),
            component: (
                <LandingPagesTable
                    landingPages={landingPages}
                    isLoading={isLoading}
                    error={error}
                    archived={showArchived}
                />
            ),
        },
    ];

    const {user} = useGetSessionUser();

    return (
        <PageContainer
            title='Landing pages'
            pageTitleBar={
                <PageTitleBar
                    title='Landing pages'
                    subheaderPrimaryText='Design & build landing pages that drive conversions'
                    buttons={
                        <ButtonGroup>
                            {user && user.permissions.canAccessSetup && (
                                <Button
                                    icon={CogIcon}
                                    size='big'
                                    onClick={() => {
                                        appHistory.push('/marketing/landing-pages-settings');
                                    }}
                                >
                                    Settings
                                </Button>
                            )}
                            <CreatePageButton initialOpen={shouldAutoCreate} />
                        </ButtonGroup>
                    }
                />
            }
            controlBar={
                <ListPageControlBar
                    archiveTab={{
                        isActive: showArchived,
                        onChange: () => {
                            props.onUpdateFilters({
                                archived: showArchived ? null : true,
                            });
                        },
                    }}
                    tabs={{
                        selectedTab: selectedTab,
                        options: tabOptions,
                    }}
                />
            }
        >
            <GetInstanceInfo>
                {/* @ts-expect-error */}
                {({sessionInstance}) => {
                    return landingPages.length > 0 &&
                        sessionInstance &&
                        !sessionInstance.hasLandingPages ? (
                        <LandingPagesUpsellBanner
                            canAccessBilling={user ? user.permissions.canAccessBilling : false}
                        />
                    ) : null;
                }}
            </GetInstanceInfo>
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={selectedTab} tabOptions={tabOptions} />
        </PageContainer>
    );
}
